/**
 * CRM権限チェック hooks
 *
 * CRMライセンスとcrm_roleの組み合わせで各操作の可否を判定する。
 * CRMページ・コンポーネント全体で一貫した権限チェックを行うための基盤。
 */
import { useContext, useMemo } from "react";
import { hasLicense, LICENSE_KEY } from "../constants/licenses";
import { getCurrentTenant } from "../utils/authority-utils";
import { type CrmRole, hasCrmRoleOrAbove } from "../utils/crm-utils";
import { AuthContext } from "./authProvider/authProvider";

export type { CrmRole } from "../utils/crm-utils";

/**
 * CRM権限情報
 */
export type CrmPermission = {
  /** CRMにアクセス可能か（ライセンス有 + crmRole非null） */
  hasCrmAccess: boolean;
  /** 作成・編集が可能か（editor以上） */
  canEdit: boolean;
  /** 削除が可能か（admin） */
  canDelete: boolean;
  /** 契約操作が可能か（admin） */
  canManageContracts: boolean;
  /** CRM admin か */
  isAdmin: boolean;
  /** 現在のCRMロール */
  crmRole: CrmRole | null;
};

const NO_PERMISSION: CrmPermission = {
  hasCrmAccess: false,
  canEdit: false,
  canDelete: false,
  canManageContracts: false,
  isAdmin: false,
  crmRole: null,
};

/**
 * CRM権限チェック hooks
 * @return {CrmPermission} CRM権限情報
 */
export function useCrmPermission(): CrmPermission {
  const { currentUser } = useContext(AuthContext);

  return useMemo(() => {
    const tenant = getCurrentTenant(currentUser);
    if (!tenant) return NO_PERMISSION;

    const hasCrmLicense = hasLicense(tenant.licenses, LICENSE_KEY.CRM);
    const crmRole = tenant.crmRole as CrmRole | null;

    if (!hasCrmLicense || !crmRole) return NO_PERMISSION;

    return {
      hasCrmAccess: true,
      canEdit: hasCrmRoleOrAbove(crmRole, "editor"),
      canDelete: hasCrmRoleOrAbove(crmRole, "admin"),
      canManageContracts: hasCrmRoleOrAbove(crmRole, "admin"),
      isAdmin: hasCrmRoleOrAbove(crmRole, "admin"),
      crmRole,
    };
  }, [currentUser]);
}
